import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { AlertCircle, CheckCircle, Info, X, AlertTriangle } from 'lucide-react'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number
  persistent?: boolean
}

interface NotificationContextType {
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id'>) => string
  removeNotification: (id: string) => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

// Convenience hooks
export const useNotify = () => {
  const { addNotification } = useNotifications()

  return {
    success: (title: string, message?: string, options?: Partial<Notification>) =>
      addNotification({ type: 'success', title, ...(message !== undefined && { message }), ...options }),
    error: (title: string, message?: string, options?: Partial<Notification>) =>
      addNotification({ type: 'error', title, ...(message !== undefined && { message }), ...options }),
    warning: (title: string, message?: string, options?: Partial<Notification>) =>
      addNotification({ type: 'warning', title, ...(message !== undefined && { message }), ...options }),
    info: (title: string, message?: string, options?: Partial<Notification>) =>
      addNotification({ type: 'info', title, ...(message !== undefined && { message }), ...options })
  }
}

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }, [])

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newNotification: Notification = {
      id,
      duration: 5000,
      persistent: false,
      ...notification
    }

    setNotifications(prev => [...prev, newNotification])

    // Auto-remove non-persistent notifications
    if (!newNotification.persistent && newNotification.duration! > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }, [removeNotification])

  const clearAll = useCallback(() => {
    setNotifications([])
  }, [])

  return (
    <NotificationContext.Provider
      value={{ notifications, addNotification, removeNotification, clearAll }}
    >
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  )
}

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotifications()

  if (notifications.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  )
}

interface NotificationItemProps {
  notification: Notification
  onClose: () => void
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(onClose, 250)
  }

  const iconForType: Record<NotificationType, React.ElementType> = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  }

  const toneMap: Record<NotificationType, string> = {
    success: '--color-success',
    error: '--color-danger',
    warning: '--color-accent',
    info: '--color-primary'
  }

  const accentVar = toneMap[notification.type]
  const accentColor = `var(${accentVar})`
  const IconComponent = iconForType[notification.type]

  const toneStyle = {
    borderColor: `color-mix(in srgb, ${accentColor} 30%, transparent)`,
    backgroundColor: `color-mix(in srgb, ${accentColor} 12%, var(--color-surface))`
  }

  return (
    <div
      className={`
        relative border rounded-xl shadow-brand-soft p-4 transition-all duration-300 ease-in-out backdrop-blur
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      style={toneStyle}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <IconComponent className="h-5 w-5" style={{ color: accentColor }} />
        </div>

        <div className="ml-3 flex-1">
          <h4 className="text-sm font-semibold text-strong">{notification.title}</h4>
          {notification.message && (
            <p className="mt-1 text-sm text-muted">{notification.message}</p>
          )}
        </div>

        <button
          onClick={handleClose}
          className="ml-4 inline-flex text-muted hover:text-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)] rounded-full"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
